import { useCallback, useEffect, useState } from "react";
import { Crown, Check, Infinity as InfinityIcon, BellRing, Star, Copy, Loader2, PartyPopper, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import CardForm from "@/components/CardForm";

const BENEFITS = [
  [InfinityIcon, "Pedidos ilimitados por 30 dias"],
  [BellRing, "Avisos de pedidos por notificação, e-mail e WhatsApp"],
  [Star, "Selo Pro no seu perfil"],
];
const fmt = (d) => new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

function usePaidPolling(id, onPaid) {
  useEffect(() => {
    if (!id) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/pagamentos/${id}/status`);
        if (data.status === "paid") { clearInterval(t); onPaid(data); }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(t);
  }, [id, onPaid]);
}

function CardPending({ pay, onPaid }) {
  usePaidPolling(pay.id, onPaid);
  return (
    <div className="po-card flex items-center gap-3 border-2 border-yellow-400 bg-yellow-50" data-testid="card-pending">
      <Loader2 className="h-7 w-7 animate-spin text-yellow-700" />
      <p className="text-lg font-bold text-yellow-900">Pagamento em análise, assim que aprovar liberamos seu Pro.</p>
    </div>
  );
}

function PixBox({ pay, onPaid }) {
  usePaidPolling(pay.id, onPaid);
  const copy = async () => {
    try { await navigator.clipboard.writeText(pay.qr_code); toast.success("Código Pix copiado!"); }
    catch (e) { toast.error("Não foi possível copiar. Selecione o código e copie manualmente."); }
  };
  return (
    <div className="po-card space-y-4 text-center" data-testid="pix-box">
      <p className="flex items-center justify-center gap-2 text-xl font-bold text-slate-900"><Smartphone className="h-6 w-6 text-orange-500" /> Pague pelo app do seu banco</p>
      <img src={`data:image/png;base64,${pay.qr_code_base64}`} alt="QR Code Pix" className="mx-auto h-60 w-60 rounded-2xl border border-slate-200" data-testid="pix-qr-image" />
      <button onClick={copy} className="po-btn-primary w-full" data-testid="pix-copy-button"><Copy className="h-6 w-6" /> Copiar código Pix</button>
      <textarea readOnly value={pay.qr_code} rows={3} onFocus={(e) => e.target.select()} className="w-full rounded-2xl border-2 border-slate-200 p-3 text-sm text-slate-600" data-testid="pix-copy-paste" />
      <p className="flex items-center justify-center gap-2 text-base text-slate-600" data-testid="pix-waiting"><Loader2 className="h-5 w-5 animate-spin" /> Aguardando o pagamento... O Pro é liberado sozinho assim que o Pix for aprovado.</p>
      <p className="text-sm text-slate-500">Código válido até {new Date(pay.expires_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.</p>
    </div>
  );
}

export default function ProPlan() {
  const { user, refresh } = useAuth();
  const p = user.provider;
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);
  const [method, setMethod] = useState("pix");
  const [cardPending, setCardPending] = useState(null);
  const [cardError, setCardError] = useState("");
  const limit = p?.free_limit ?? 5;
  const left = Math.max(0, limit - (p?.accepted_count || 0));

  const subscribe = async () => {
    setBusy(true);
    try { setPay((await api.post("/pagamentos/pix")).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const onPaid = useCallback(async (data) => { setPay(null); setCardPending(null); setPaid(data); toast.success("Pagamento aprovado! Plano Pro liberado."); await refresh(); }, [refresh]);
  const onCard = (data) => {
    setCardError("");
    if (data.result === "approved") onPaid(data);
    else if (data.result === "pending") setCardPending(data);
    else setCardError(data.message || "Cartão recusado, tente outro cartão ou pague por Pix.");
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 sm:px-6">
      <BackButton to="/perfil" />
      <div className="rounded-3xl bg-slate-800 p-6 text-white sm:p-8">
        <Crown className="h-12 w-12 text-orange-400" />
        <h1 className="mt-3 font-display text-3xl font-extrabold sm:text-4xl">Plano Pro</h1>
        <p className="mt-2 text-lg text-slate-200">Os {limit} primeiros pedidos aceitos são grátis. Depois, assine o Pro para continuar recebendo pedidos.</p>
        {p && !p.is_pro && <p className="mt-4 rounded-2xl bg-white/10 p-4 text-lg font-semibold" data-testid="pro-counter">Pedidos grátis restantes: {left} de {limit}</p>}
        {p?.is_pro && <p className="mt-4 rounded-2xl bg-orange-500 p-4 text-lg font-bold" data-testid="pro-active">Plano Pro ativo até {fmt(p.pro_until)}.</p>}
      </div>
      {paid && (
        <div className="po-card flex items-center gap-3 border-2 border-green-500 bg-green-50" data-testid="pix-paid">
          <PartyPopper className="h-8 w-8 text-green-600" />
          <p className="text-lg font-bold text-green-800">Pagamento aprovado! Seu Plano Pro está ativo até {fmt(paid.pro_until)}.</p>
        </div>
      )}
      {!p && <p className="po-card text-lg">Cadastre-se como prestador para assinar o Plano Pro.</p>}
      {p && !paid && (
        <div className="grid grid-cols-2 gap-3" role="tablist" data-testid="pay-method-toggle">
          {[["pix", "Pix"], ["card", "Cartão"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`pay-method-${v}`}
              className={`min-h-[60px] rounded-2xl border-2 text-xl font-bold ${method === v ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 text-slate-700"}`}>{l}</button>
          ))}
        </div>
      )}
      {p && !paid && method === "card" && (
        <>
          {cardError && <p className="po-card border-2 border-red-300 bg-red-50 text-lg font-bold text-red-700" data-testid="card-error">{cardError}</p>}
          {cardPending ? <CardPending pay={cardPending} onPaid={onPaid} /> : <CardForm onResult={onCard} />}
        </>
      )}
      {p && method === "pix" && (pay ? <PixBox pay={pay} onPaid={onPaid} /> : (
        <div className="po-card">
          <ul className="space-y-3">
            {BENEFITS.map(([I, t]) => <li key={t} className="flex items-center gap-3 text-lg text-slate-800"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600"><I className="h-6 w-6" /></span>{t}</li>)}
          </ul>
          <p className="mt-5 font-display text-3xl font-extrabold text-slate-900" data-testid="pro-price">R$ 11,99<span className="text-xl font-bold text-slate-600">/mês</span></p>
          <p className="text-base text-slate-600">Pagamento por Pix. Cada pagamento libera 30 dias. Sem renovação automática.</p>
          <button onClick={subscribe} disabled={busy} className="po-btn-primary mt-5 min-h-[68px] w-full text-xl" data-testid="pro-subscribe-button">
            {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <Check className="h-7 w-7" />}
            {p.is_pro ? "Renovar Plano Pro - R$ 11,99 (+30 dias)" : "Assinar Plano Pro - R$ 11,99/mês"}
          </button>
        </div>
      ))}
    </div>
  );
}
